<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useOrganizationStore } from '@/stores/organizationStore'

const route = useRoute()
const router = useRouter()
const orgStore = useOrganizationStore()
const organizationId = route.params.id

// Billing Cycle State ('monthly' or 'yearly')
const billingCycle = ref('monthly')

// Fetch organization data
onMounted(async () => {
  if (orgStore.organizations.length === 0) {
    await orgStore.fetchOrganizations()
  }

  // Check for 'success=true' in the URL query parameters
  if (route.query.success === 'true') {
    await orgStore.syncSubscription(organizationId)
    // Check if status is active and redirect to app
    const org = orgStore.getOrganizationById(organizationId)
    if (org?.status === 'active') {
      router.push(`/app/${organizationId}`)
    }
  }
})

// Computed Organization Data
const organization = computed(() => orgStore.getOrganizationById(organizationId))

// Helper to format plan name
const planName = computed(() => {
  const plan = organization.value?.subscription?.plan || 'Pay as you go'
  return plan.replace('_', ' ')
})

const planStatus = computed(() => organization.value?.status || 'Unknown')

// Pricing Logic for Pro Plan
const proPrice = computed(() => billingCycle.value === 'yearly' ? '$20' : '$24')
const proBillingText = computed(() => billingCycle.value === 'yearly' ? '$240 billed annually' : 'Billed monthly')

// Action Handlers
const handleSubscribe = async (tier) => {
  let plan = tier
  if (tier === 'pro') {
    plan = billingCycle.value === 'monthly' ? 'pro_monthly' : 'pro_yearly'
  }

  try {
    const response = await orgStore.createSubscriptionSession(organizationId, plan)
    if (response && response.sessionURL) {
      window.location.href = response.sessionURL
    }
  } catch (error) {
    console.error("Failed to create subscription session:", error)
  }
}

const handleContactUs = () => {
  window.open('https://forms.gle/rxaHvVDX5Vj6oWRY6', '_blank')
}
</script>

<template>
  <div class="space-y-12">
    <div class="border-b border-b-gray-100 pb-4">
      <h1 class="text-xl text-gray-900">Billing</h1>
      <p class="text-md font-light text-gray-500 mt-1">Manage organization billing and subscription</p>
    </div>

    <div class="bg-white rounded-xl border border-gray-100 shadow-sm">
      <div class="px-6 py-5 border-b border-gray-100">
        <h2 class="text-lg text-gray-900">Plan</h2>
      </div>
      <div class="p-6">
        <p v-if="organization?.status !== 'incomplete'" class="mb-6 text-sm text-gray-700 leading-relaxed">
          The current plan of your organization is <strong class="capitalize">'{{ planName }}'</strong> and
          <strong>{{ planStatus }}</strong>. If you have canceled your subscription recently, it will be effective on
          the next billing cycle.
          If you want to switch from 'Pay as you go' model to 'Pro' model or viseversa please <a
            href="mailto:hi@hasmcp.com" class="underline">contact us</a>.
          <span v-if="organization?.subscription?.seats">
            You have <strong>{{ organization.subscription?.seats }}</strong> seats allocated.
          </span>
          All subscription managements are handled in Stripe portal.
        </p>
        <p v-else class="mb-6 text-sm text-gray-700 leading-relaxed">Unsure about plans? At any time, you can upgrade or
          downgrade your plan
          based on your organization's needs.</p>

        <div class="flex justify-center mb-10">
          <div class="bg-gray-100 p-1 rounded-lg inline-flex">
            <button @click="billingCycle = 'monthly'" :class="[
              'px-6 py-1.5 text-sm font-medium rounded-md transition',
              billingCycle === 'monthly' ? 'bg-black text-white shadow-sm' : 'text-gray-500 hover:text-black'
            ]">
              Monthly
            </button>
            <button @click="billingCycle = 'yearly'" :class="[
              'px-6 py-1.5 text-sm font-medium rounded-md transition',
              billingCycle === 'yearly' ? 'bg-black text-white shadow-sm' : 'text-gray-500 hover:text-black'
            ]">
              Yearly
            </button>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div
            class="p-6 rounded-xl border border-gray-200 bg-white hover:shadow-xl hover:border-gray-300 transition duration-300 group">
            <h3 class="text-xl font-bold mb-2 text-gray-900">Cloud Hobby</h3>
            <p class="text-sm text-gray-500 mb-6 min-h-10">Start free and pay for what you use extra</p>
            <div class="mb-6">
              <span class="text-4xl font-extrabold text-gray-900">$0</span>
              <span class="text-gray-500 text-xs font-medium"> / month</span>
              <p class="text-xs text-gray-400 mt-1">billed at the end of the month</p>
            </div>

            <div class="h-20">
              <button @click="handleSubscribe('hobby')"
                class="w-full bg-white text-black border border-gray-200 py-3 rounded-lg font-bold mb-6 hover:bg-gray-50 transition shadow-sm">
                {{ organization?.status === 'active' ? 'Manage' : 'Subscribe' }}
              </button>
            </div>

            <div class="space-y-3 text-sm text-gray-600">
              <p class="flex items-center"><svg
                  class="w-4 h-4 mr-2 text-white bg-gray-300 rounded-full p-0.5 group-hover:bg-gray-400 transition"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                </svg> 50 MCP servers</p>
              <p class="flex items-center"><svg
                  class="w-4 h-4 mr-2 text-white bg-gray-300 rounded-full p-0.5 group-hover:bg-gray-400 transition"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                </svg> Free 250 tool calls every month</p>
              <p class="flex items-start"><svg
                  class="w-4 h-4 mr-2 mt-0.5 text-white bg-gray-300 rounded-full p-0.5 group-hover:bg-gray-400 transition shrink-0"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                </svg> Additional 5k tool calls for $10</p>
              <p class="flex items-center"><svg
                  class="w-4 h-4 mr-2 text-white bg-gray-300 rounded-full p-0.5 group-hover:bg-gray-400 transition"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                </svg> Single user</p>
              <p class="flex items-center"><svg
                  class="w-4 h-4 mr-2 text-white bg-gray-300 rounded-full p-0.5 group-hover:bg-gray-400 transition"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                </svg> MCP Auth</p>
              <p class="flex items-center"><svg
                  class="w-4 h-4 mr-2 text-white bg-gray-300 rounded-full p-0.5 group-hover:bg-gray-400 transition"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                </svg> Token optimization with Jmespath</p>
              <p class="flex items-center"><svg
                  class="w-4 h-4 mr-2 text-white bg-gray-300 rounded-full p-0.5 group-hover:bg-gray-400 transition"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                </svg> Token optimization with JS</p>
            </div>
          </div>

          <div
            class="p-6 rounded-xl border border-gray-200 bg-white hover:shadow-xl hover:border-gray-300 transition duration-300 group">
            <h3 class="text-xl font-bold mb-2 text-gray-900">Cloud Pro</h3>
            <p class="text-sm text-gray-500 mb-6 min-h-10">For small teams and startups</p>
            <div class="mb-6">
              <span class="text-4xl font-extrabold text-gray-900">{{ proPrice }}</span>
              <span class="text-gray-500 text-xs font-medium"> per seat / month</span>
              <p class="text-xs text-gray-400 mt-1">{{ proBillingText }}</p>
            </div>
            <div class="h-20">
              <button @click="handleSubscribe('pro')"
                class="w-full bg-black text-white py-3 rounded-lg font-bold mb-6 hover:bg-gray-800 transition shadow-md">{{
                  organization?.status !== 'incomplete' ? 'Manage' : 'Subscribe' }}</button>
            </div>
            <div class="space-y-3 text-sm text-gray-600">
              <p class="flex items-center"><svg
                  class="w-4 h-4 mr-2 text-white bg-gray-300 rounded-full p-0.5 group-hover:bg-gray-400 transition"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                </svg> 100 active MCP servers</p>
              <p class="flex items-center"><svg
                  class="w-4 h-4 mr-2 text-white bg-gray-300 rounded-full p-0.5 group-hover:bg-gray-400 transition"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                </svg> 100k tool calls per seat</p>
              <p class="flex items-center"><svg
                  class="w-4 h-4 mr-2 text-white bg-gray-300 rounded-full p-0.5 group-hover:bg-gray-400 transition"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                </svg> Custom domains</p>
              <p class="flex items-center"><svg
                  class="w-4 h-4 mr-2 text-white bg-gray-300 rounded-full p-0.5 group-hover:bg-gray-400 transition"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                </svg> MCP Auth</p>
              <p class="flex items-center"><svg
                  class="w-4 h-4 mr-2 text-white bg-gray-300 rounded-full p-0.5 group-hover:bg-gray-400 transition"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                </svg> Token optimization with Jmespath</p>
              <p class="flex items-center"><svg
                  class="w-4 h-4 mr-2 text-white bg-gray-300 rounded-full p-0.5 group-hover:bg-gray-400 transition"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                </svg> Token optimization with JS</p>
            </div>
          </div>

          <div
            class="p-6 rounded-xl border border-gray-200 bg-white hover:shadow-xl hover:border-gray-300 transition duration-300 group">
            <h3 class="text-xl font-bold mb-2 text-gray-900">Enterprise</h3>
            <p class="text-sm text-gray-500 mb-6 min-h-10">Isolated environment</p>
            <div class="mb-6">
              <span class="text-4xl font-extrabold text-gray-900">Contact us</span>
              <p class="text-xs text-gray-400 mt-1">&nbsp;</p>
            </div>
            <div class="h-20">
              <button @click="handleContactUs"
                class="w-full bg-black text-white py-3 rounded-lg font-bold mb-6 hover:bg-gray-800 transition shadow-md">Contact</button>
            </div>
            <div class="space-y-3 text-sm text-gray-600">
              <p class="flex items-center"><svg
                  class="w-4 h-4 mr-2 text-white bg-gray-300 rounded-full p-0.5 group-hover:bg-gray-400 transition"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                </svg> Unlimited MCP Servers</p>
              <p class="flex items-center"><svg
                  class="w-4 h-4 mr-2 text-white bg-gray-300 rounded-full p-0.5 group-hover:bg-gray-400 transition"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                </svg> 100M tool calls</p>
              <p class="flex items-center"><svg
                  class="w-4 h-4 mr-2 text-white bg-gray-300 rounded-full p-0.5 group-hover:bg-gray-400 transition"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                </svg> Unlimited teams and users</p>
              <p class="flex items-center"><svg
                  class="w-4 h-4 mr-2 text-white bg-gray-300 rounded-full p-0.5 group-hover:bg-gray-400 transition"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                </svg> Custom domains</p>
              <p class="flex items-center"><svg
                  class="w-4 h-4 mr-2 text-white bg-gray-300 rounded-full p-0.5 group-hover:bg-gray-400 transition"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                </svg> MCP Auth</p>
              <p class="flex items-center"><svg
                  class="w-4 h-4 mr-2 text-white bg-gray-300 rounded-full p-0.5 group-hover:bg-gray-400 transition"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                </svg> Token optimization with Jmespath</p>
              <p class="flex items-center"><svg
                  class="w-4 h-4 mr-2 text-white bg-gray-300 rounded-full p-0.5 group-hover:bg-gray-400 transition"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                </svg> Token optimization with JS</p>
              <p class="flex items-center"><svg
                  class="w-4 h-4 mr-2 text-white bg-gray-300 rounded-full p-0.5 group-hover:bg-gray-400 transition"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                </svg> Audit logs</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>